import '@uiw/react-markdown-preview/markdown.css';
import '@uiw/react-md-editor/markdown-editor.css';
import MDEditor from '@uiw/react-md-editor';
import {
    Input,
    OutlinedInput,
    OutlinedInputProps,
    styled,
    SxProps,
    Theme,
    useTheme,
} from '@mui/material';
import React from 'react';
import ReactMarkdown from 'react-markdown';
import characters from 'src/constants/characters';
import { ClickElementType } from 'src/lib/interactionTypes';
import { Typography } from '../Typography';
import { Field, FieldProps } from './Field';
import withFormConsumer from './withFormConsumer';

type TextFieldVariant = 'outlined' | 'text';
type TextFieldEditor = 'plain' | 'markdown';

const MarkdownEditorWrapper = styled('div')(({ theme }) => ({
    '.w-md-editor': {
        backgroundColor: theme.resqPalette.surface.default,
        borderColor: theme.resqPalette.border.subdued,
        borderRadius: theme.shape.borderRadius,
        '--color-border-default': theme.resqPalette.border.subdued,
        '--color-canvas-default': theme.resqPalette.surface.default,
        '--color-fg-default': theme.resqPalette.text.default,
    },
    '.w-md-editor-toolbar': {
        backgroundColor: theme.resqPalette.surface.default,
        borderColor: theme.resqPalette.border.subdued,
    },
    '.w-md-editor-content': {
        backgroundColor: theme.resqPalette.surface.default,
    },
    '.w-md-editor-text-input, .w-md-editor-text-pre': {
        color: theme.resqPalette.text.default,
    },
    '.wmde-markdown': {
        backgroundColor: theme.resqPalette.surface.default,
        color: theme.resqPalette.text.default,
    },
}));

const ReadOnlyMarkdown = styled(ReactMarkdown)(({ theme }) => ({
    color: theme.resqPalette.text.default,
    overflowWrap: 'break-word',
    '& > :first-of-type': {
        marginTop: 0,
    },
    '& > :last-child': {
        marginBottom: 0,
    },
}));

export interface TextFieldProps
    extends
        Pick<
            FieldProps,
            | 'editable'
            | 'error'
            | 'helperText'
            | 'helperTextStyle'
            | 'label'
            | 'labelAdornment'
            | 'inlineLabelAdornment'
            | 'tooltip'
            | 'wide'
            | 'required'
            | 'errorId'
        >,
        Pick<
            OutlinedInputProps,
            | 'autoComplete'
            | 'autoFocus'
            | 'className'
            | 'defaultValue'
            | 'disabled'
            | 'id'
            | 'multiline'
            | 'name'
            | 'onMouseDown'
            | 'placeholder'
            | 'rows'
            | 'maxRows'
            | 'style'
            | 'type'
            | 'onBlur'
            | 'onChange'
            | 'onClick'
            | 'onKeyDown'
            | 'value'
            | 'size'
            | 'startAdornment'
            | 'endAdornment'
            | 'inputProps'
            | 'inputRef'
        > {
    trimWhiteSpace?: boolean;
    variant?: TextFieldVariant;
    editor?: TextFieldEditor;
    emptyNode?: React.ReactNode;
    inputSx?: SxProps<Theme>;
    autoCapitalize?: string;
    autoCorrect?: string;
    spellCheck?: boolean;
}

export const Text = ({
    editable = true,
    error,
    helperText,
    helperTextStyle,
    label,
    labelAdornment,
    inlineLabelAdornment,
    tooltip,
    value,
    onChange,
    trimWhiteSpace = false,
    wide = false,
    required = false,
    errorId,
    variant = 'outlined',
    editor = 'plain',
    emptyNode = characters.emdash,
    inputSx,
    autoCapitalize,
    autoCorrect,
    spellCheck,
    // @ts-expect-error - disableUnderline is used by some text-variant callers; drop it from the outlined path
    disableUnderline,
    inputProps: inputElementProps,
    // @ts-expect-error - InputProps is not in TextFieldProps but may be passed by callers; destructure to prevent DOM leak
    InputProps: _InputProps,
    ...inputProps
}: TextFieldProps) => {
    const theme = useTheme();
    const isMarkdownEditor = editor === 'markdown';
    const stringValue = String(value ?? '');
    const baseOutlinedSx: SxProps<Theme> = {
        backgroundColor: (theme) => theme.resqPalette.surface.default,
        color: (theme) => theme.resqPalette.text.default,
        '& .MuiOutlinedInput-input, & .MuiInputBase-input': {
            color: (theme) => theme.resqPalette.text.default,
            '&::placeholder': {
                color: (theme) => theme.resqPalette.text.placeHolder,
                WebkitTextFillColor: (theme) => theme.resqPalette.text.placeHolder,
                opacity: 1,
            },
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: (theme) => theme.resqPalette.border.focused,
            borderWidth: 2,
        },
        '&.Mui-disabled': {
            backgroundColor: (theme) => theme.resqPalette.surface.subdued,
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: (theme) => theme.resqPalette.border.subdued,
            },
            '& .MuiOutlinedInput-input, & .MuiInputBase-input': {
                backgroundColor: 'transparent',
                color: (theme) =>
                    theme.palette.mode === 'dark'
                        ? theme.resqPalette.text.subdued
                        : theme.resqPalette.text.disabled,
                WebkitTextFillColor: (theme) =>
                    theme.palette.mode === 'dark'
                        ? theme.resqPalette.text.subdued
                        : theme.resqPalette.text.disabled,
            },
        },
    };
    const resolvedOutlinedSx: SxProps<Theme> = inputSx
        ? ([baseOutlinedSx, inputSx] as SxProps<Theme>)
        : baseOutlinedSx;

    return (
        <Field
            editable={editable}
            error={error}
            label={label}
            labelAdornment={labelAdornment}
            inlineLabelAdornment={inlineLabelAdornment}
            tooltip={tooltip}
            helperText={helperText}
            helperTextStyle={helperTextStyle}
            wide={wide}
            required={required}
            errorId={errorId}
            elementType={ClickElementType.TEXT_INPUT}
            name={inputProps.name}
        >
            {!editable ? (
                isMarkdownEditor && stringValue ? (
                    <ReadOnlyMarkdown>{stringValue}</ReadOnlyMarkdown>
                ) : (
                    <Typography role="body" sx={{ overflowWrap: 'break-word' }}>
                        {value ? String(value) : emptyNode}
                    </Typography>
                )
            ) : isMarkdownEditor ? (
                <MarkdownEditorWrapper data-color-mode={theme.palette.mode}>
                    <MDEditor
                        value={stringValue}
                        onChange={(newValue, event) => {
                            if (event) {
                                onChange?.(event);
                                return;
                            }
                            onChange?.((newValue ?? '') as any);
                        }}
                        preview="edit"
                        height={inputProps.rows ? Number(inputProps.rows) * 48 : 200}
                        textareaProps={{
                            autoCapitalize,
                            autoCorrect,
                            autoFocus: inputProps.autoFocus,
                            disabled: inputProps.disabled,
                            id: inputProps.id,
                            name: inputProps.name,
                            placeholder: inputProps.placeholder,
                            spellCheck,
                            onBlur: (event) => {
                                if (trimWhiteSpace && onChange) {
                                    event.target.value = event.target.value.trim();
                                    onChange(event);
                                }
                                inputElementProps?.onBlur?.(event);
                                inputProps.onBlur?.(event as any);
                            },
                        }}
                    />
                </MarkdownEditorWrapper>
            ) : variant === 'text' ? (
                <Input
                    error={!!error}
                    fullWidth
                    disableUnderline={disableUnderline}
                    inputProps={{
                        ...inputElementProps,
                        autoCapitalize,
                        autoCorrect,
                        spellCheck,
                        onBlur: (event) => {
                            if (trimWhiteSpace && onChange) {
                                event.target.value = event.target.value.trim();
                                onChange(event);
                            }
                            inputElementProps?.onBlur?.(event);
                        },
                    }}
                    value={value}
                    onChange={onChange}
                    {...inputProps}
                />
            ) : (
                <OutlinedInput
                    sx={resolvedOutlinedSx}
                    error={!!error}
                    fullWidth
                    inputProps={{
                        ...inputElementProps,
                        autoCapitalize,
                        autoCorrect,
                        spellCheck,
                        onBlur: (event) => {
                            if (trimWhiteSpace && onChange) {
                                event.target.value = event.target.value.trim();
                                onChange(event);
                            }
                            inputElementProps?.onBlur?.(event);
                        },
                    }}
                    value={value}
                    onChange={onChange}
                    {...inputProps}
                />
            )}
        </Field>
    );
};
export const TextField = withFormConsumer<NonNullable<OutlinedInputProps['value']>, TextFieldProps>(
    Text,
);
